import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle2, LogIn } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import PasswordInput from '../components/common/PasswordInput';

interface LocationState {
  message?: string;
  from?: { pathname: string };
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, needsSetup } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const state = location.state as LocationState;
  const from = state?.from?.pathname || '/';

  useEffect(() => {
    if (state?.message) {
      setSuccessMessage(state.message);
      window.history.replaceState({}, document.title);
    }
  }, [state?.message]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
    if (needsSetup) {
      navigate('/setup', { replace: true });
    }
  }, [isAuthenticated, needsSetup, navigate, from]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      setError('Please enter your username and password');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    const result = await login(username, password);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error || 'Login failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mb-4 shadow-glow">
            <Shield className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-bold text-text-header tracking-tight">Budget</h1>
          <p className="mt-2 text-text-muted">Sign in to your encrypted vault</p>
        </div>

        {/* Login Card */}
        <div className="card bg-background-card/70 backdrop-blur-xl">
          {successMessage && (
            <div className="mb-6 p-4 rounded-xl border border-success/20 bg-success-bg flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0" />
              <p className="text-sm text-success">{successMessage}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-text-muted mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-field"
                placeholder="Enter your username"
                autoComplete="username"
                autoFocus
                disabled={isSubmitting}
              />
            </div>

            <PasswordInput
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isSubmitting}
            />

            <button type="submit" disabled={isSubmitting} className="btn btn-primary w-full shadow-glow">
              <LogIn className="w-4 h-4" />
              {isSubmitting ? 'Signing in…' : 'Sign In'}
            </button>
            
            <div className="pt-2">
              <button 
                type="button" 
                onClick={() => navigate('/setup')} 
                className="w-full text-sm text-primary hover:text-primary-hover hover:underline transition-colors"
              >
                Create new account
              </button>
            </div>
          </form>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center text-sm text-text-muted">
            <Shield className="w-4 h-4 mr-2" />
            Secured with TLS 1.3
          </div>
        </div>
      </div>
    </div>
  );
}
